import { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { Tag, Clock, BadgePercent, Sparkles, Store, MapPinned, PackageCheck } from "lucide-react";
import { Reveal } from "./Reveal";

const QUALITY_IMG =
  "https://images.pexels.com/photos/16142328/pexels-photo-16142328.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const Counter = ({ to, suffix = "+", duration = 1800 }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const [val, setVal] = useState(0);

  useEffect(() => {
    if (!inView) return;
    let raf;
    const start = performance.now();
    const tick = (now) => {
      const p = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(Math.round(eased * to));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, to, duration]);

  return (
    <span ref={ref} className="font-display text-6xl leading-none text-brand-bone md:text-7xl">
      {val.toLocaleString()}
      <span className="text-brand-volt">{suffix}</span>
    </span>
  );
};

const stats = [
  { icon: Store, to: 100, label: "Brightspot Stores" },
  { icon: MapPinned, to: 5, label: "Cities Live" },
  { icon: PackageCheck, to: 3000, label: "Products" },
];

const FeatureCard = ({ icon: Icon, title, desc, className = "", testid }) => (
  <motion.div
    data-testid={testid}
    whileHover={{ y: -8 }}
    transition={{ type: "spring", stiffness: 300, damping: 22 }}
    className={
      "group relative flex flex-col justify-between overflow-hidden rounded-2xl border border-brand-line bg-brand-surface p-8 transition-colors duration-300 hover:border-brand-volt/60 " +
      className
    }
  >
    <span className="flex h-12 w-12 items-center justify-center rounded-full border border-brand-line bg-brand-elevated text-brand-volt transition-colors duration-300 group-hover:bg-brand-volt group-hover:text-brand-bg">
      <Icon size={20} />
    </span>
    <div className="mt-10">
      <h3 className="text-2xl font-medium tracking-tight text-brand-bone">{title}</h3>
      <p className="mt-2 text-sm leading-relaxed text-brand-muted">{desc}</p>
    </div>
  </motion.div>
);

export const WhyUnique = () => {
  return (
    <section
      id="unique"
      data-testid="unique-section"
      className="mx-auto max-w-[1400px] px-6 py-24 md:px-12 md:py-36"
    >
      <div className="mb-14 flex flex-col items-start justify-between gap-6 md:flex-row md:items-end">
        <Reveal>
          <h2 className="font-display text-6xl uppercase leading-[0.9] tracking-tight md:text-7xl">
            Why we&apos;re
            <br />
            <span className="text-brand-volt">unique</span>
          </h2>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="max-w-sm text-base text-brand-muted">
            Local roots, global standards. Everything a modern neighborhood deserves — delivered in
            minutes.
          </p>
        </Reveal>
      </div>

      {/* Tetris bento grid */}
      <div className="grid grid-cols-1 gap-5 md:grid-cols-12">
        <Reveal className="md:col-span-4" y={50}>
          <FeatureCard
            testid="feature-best-prices"
            icon={Tag}
            title="Best Prices"
            desc="We negotiate the sharpest local prices so your basket always costs less."
            className="h-full min-h-[240px]"
          />
        </Reveal>

        <Reveal className="md:col-span-4" y={50} delay={0.05}>
          <FeatureCard
            testid="feature-delivery"
            icon={Clock}
            title="Within 1 Hr Delivery"
            desc="From a Brightspot near you to your doorstep — in minutes, not days."
            className="h-full min-h-[240px]"
          />
        </Reveal>

        {/* Quality assortment with image */}
        <Reveal className="md:col-span-4 md:row-span-2" y={50} delay={0.1}>
          <motion.div
            data-testid="feature-quality"
            whileHover={{ y: -8 }}
            transition={{ type: "spring", stiffness: 300, damping: 22 }}
            className="group relative flex h-full min-h-[240px] flex-col justify-end overflow-hidden rounded-2xl border border-brand-line"
          >
            <img
              src={QUALITY_IMG}
              alt="Fresh produce curated for quality"
              className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
            <div className="relative p-8">
              <span className="flex h-12 w-12 items-center justify-center rounded-full bg-brand-volt text-white">
                <Sparkles size={20} />
              </span>
              <h3 className="mt-6 text-2xl font-medium tracking-tight text-white">
                Quality Assortments
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-white/80">
                Local and global brands, hand-curated and quality-checked at every Brightspot.
              </p>
            </div>
          </motion.div>
        </Reveal>

        <Reveal className="md:col-span-8" y={50} delay={0.05}>
          <FeatureCard
            testid="feature-offers"
            icon={BadgePercent}
            title="Offers & Deals"
            desc="Daily deals and neighborhood-exclusive drops that make every order rewarding."
            className="h-full min-h-[200px]"
          />
        </Reveal>
      </div>

      {/* Stats */}
      <div className="mt-16 grid grid-cols-1 gap-5 sm:grid-cols-3">
        {stats.map((s, i) => (
          <Reveal key={s.label} delay={i * 0.1} y={40}>
            <div
              data-testid={`stat-${i}`}
              className="rounded-2xl border border-brand-line bg-brand-surface p-8"
            >
              <s.icon className="mb-6 text-brand-volt" size={22} />
              <Counter to={s.to} />
              <p className="mt-3 text-xs uppercase tracking-[0.25em] text-brand-muted">
                {s.label}
              </p>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
};
